import { readTable } from '../store/localDb.store'
import { MATERIAL_CODE } from './masterMaterial.handler'

/**
 * Kode material untuk "Tambah ke master" dari Pendataan Awal (v7.50 P-09, keputusan Q-12 masih OPEN).
 *
 * Keputusan user 2026-10-21 #8 (tutup D82): sistem MENGUSULKAN kode (awalan kategori + nomor urut, prototype
 * `P.idMaster`), pengguna boleh mengubahnya sebelum menyimpan; aturan 2–16 huruf / angka / minus (`^[A-Z0-9-]{2,16}$`,
 * dinormalkan huruf besar), UNIK terhadap master raw material DAN terhadap kode yang sudah diusulkan untuk baris lain
 * di impor yang sama — dicek saat pratinjau (`pd/check-code`) dan saat simpan. Kode terkunci setelah material dibuat
 * (`immutableFields.ts`: `mt_material.code`).
 */

/** Awalan kode per kategori Pendataan Awal (prototype `PREFIX`). */
export const PD_CODE_PREFIX: Record<string, string> = {
  Etiket: 'ETK',
  CTP: 'CTP',
  Foil: 'FOIL',
  OPP: 'OPP',
  Box: 'BOX',
  Inner: 'INN',
  Dospress: 'DSP',
  Lem: 'LEM',
  Filter: 'FLT',
  'Papir/Bobin': 'PPR',
  'Lain-lain': 'LNN'
}

const normalizeCode = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()

/** Usulan kode berikutnya untuk satu kategori, melewati kode yang sudah dipakai atau sedang diusulkan. */
export function proposeMaterialCode(category: string, taken: Set<string> = new Set()): string {
  const prefix = PD_CODE_PREFIX[category] ?? 'LNN'
  const used = new Set([...readTable('mt_material').map(row => normalizeCode(row.code)), ...taken])
  const pattern = new RegExp(`^${prefix}-(\\d{3,4})$`)
  let next = Math.max(0, ...[...used].map(code => Number(pattern.exec(code)?.[1] ?? 0))) + 1
  while (used.has(`${prefix}-${String(next).padStart(4, '0')}`)) next += 1
  return `${prefix}-${String(next).padStart(4, '0')}`
}

/**
 * Pesan galat, atau null bila kode boleh dipakai. `taken` = kode yang sudah dipesan di impor yang sama (Map: kode → nama
 * material pemesannya, untuk pesan yang jelas).
 */
export function materialCodeProblem(raw: unknown, taken: Set<string> | Map<string, string> = new Set()): string | null {
  const code = normalizeCode(raw)
  if (!code) return 'Kode material wajib diisi.'
  if (!MATERIAL_CODE.test(code)) return 'Kode 2–16 huruf besar/angka/tanda minus, tanpa spasi.'
  if (readTable('mt_material').some(row => normalizeCode(row.code) === code))
    return `Kode ${code} sudah dipakai material lain.`
  if (taken.has(code))
    return taken instanceof Map
      ? `Kode ${code} sudah diusulkan untuk ${taken.get(code)} di impor ini — pakai kode lain.`
      : `Kode ${code} sudah dipakai baris lain di impor ini.`
  return null
}

export { normalizeCode as normalizeMaterialCode }
